import type { AccountCapacity, AudienceSegment, RecipientsPage } from '@/features/marketing-shared';
import type { SmsAccountNumber, SmsCampaign } from '@/features/sms-marketing/model/types';

/** Destinatários como a API devolve: uma página só, com o total. */
function page<T>(rows: T[]): RecipientsPage<T> {
  return { page: 1, pageSize: 50, total: rows.length, rows };
}

/** Números fictícios da conta. O envio usa `telephone_number` em E.164. O pausado não pode ser remetente. */
export const SMS_ACCOUNT_NUMBERS: SmsAccountNumber[] = [
  { id: 'num-loja', telephoneNumber: '+12025550100', paused: false },
  { id: 'num-campanha', telephoneNumber: '+12025550199', paused: false },
  { id: 'num-pausado', telephoneNumber: '+12025550101', paused: true },
];

/** Saldo fictício de SMS, separado dos 50.000 e-mails. 28 + 72 = 100. */
export const SMS_CAPACITY: AccountCapacity = {
  planName: 'Starter',
  cycleLimit: 100,
  sent: 28,
  available: 72,
  renewsOn: '2026-11-10T12:00:00-03:00',
  transactionalReserve: 0,
  companyLimit: 80,
  campaignLimit: 60,
};

export const SMS_EMPTY_AUDIENCE_ID = 'sms-vazio';
export const DEFAULT_SMS_AUDIENCE_ID = 'sms-aniversario';

export const SMS_AUDIENCES: AudienceSegment[] = [
  {
    id: DEFAULT_SMS_AUDIENCE_ID,
    name: 'Aniversariantes com celular',
    description: 'Celular utilizável e consentimento para SMS.',
    total: 90,
    eligible: 40,
    excludedNoConsent: 22,
    excludedUnsubscribed: 12,
    excludedInvalid: 16,
  },
  {
    id: 'sms-ativos',
    name: 'Ativos em 30 dias',
    description: 'Compraram no último mês. A estimativa passa do limite interno da campanha.',
    total: 140,
    eligible: 90,
    excludedNoConsent: 24,
    excludedUnsubscribed: 14,
    excludedInvalid: 12,
  },
  {
    id: 'sms-vip',
    name: 'VIP com celular',
    description: 'Lista curta, com telefone válido.',
    total: 36,
    eligible: 24,
    excludedNoConsent: 4,
    excludedUnsubscribed: 5,
    excludedInvalid: 3,
  },
  {
    id: SMS_EMPTY_AUDIENCE_ID,
    name: 'Importação sem celular válido',
    description: 'Lista recente. Ninguém pode receber este SMS.',
    total: 210,
    eligible: 0,
    excludedNoConsent: 40,
    excludedUnsubscribed: 18,
    excludedInvalid: 152,
  },
];

export const SMS_CAMPAIGNS: SmsCampaign[] = [
  {
    id: 'sms-rascunho',
    name: 'Lembrete de pontos',
    from: '+12025550100',
    text: '{{primeiro_nome}}, você tem {{pontos}} pontos na {{loja}}.',
    status: 'draft',
    audience: {
      audienceId: DEFAULT_SMS_AUDIENCE_ID,
      audienceName: 'Aniversariantes com celular',
      total: 90,
      eligible: 40,
      excludedNoConsent: 22,
      excludedUnsubscribed: 12,
      excludedInvalid: 16,
    },
    scheduledAt: null,
    sendMode: null,
    createdAt: '2026-10-19T18:40:00-03:00',
  },
  {
    id: 'sms-agendada',
    name: 'VIP de sexta',
    from: '+12025550100',
    text: '{{primeiro_nome}}, a {{loja}} separou um mimo para você.',
    status: 'scheduled',
    audience: {
      audienceId: 'sms-vip',
      audienceName: 'VIP com celular',
      total: 36,
      eligible: 24,
      excludedNoConsent: 4,
      excludedUnsubscribed: 5,
      excludedInvalid: 3,
    },
    scheduledAt: '2026-10-24T11:00:00-03:00',
    sendMode: 'later',
    createdAt: '2026-10-18T09:00:00-03:00',
  },
  {
    id: 'sms-enviando',
    name: 'Confirmação de visita',
    from: '+12025550100',
    text: '{{primeiro_nome}}, obrigado pela visita na {{loja}}.',
    status: 'sending',
    audience: {
      audienceId: 'sms-visita',
      audienceName: 'Visitas de hoje',
      total: 18,
      eligible: 12,
      excludedNoConsent: 3,
      excludedUnsubscribed: 1,
      excludedInvalid: 2,
    },
    scheduledAt: '2026-10-20T15:00:00-03:00',
    sendMode: 'later',
    createdAt: '2026-10-20T14:10:00-03:00',
    metrics: { sent: 6, delivered: 5, rejected: 1, unsubscribed: 0 },
    recipients: page([
      {
        id: 'sms-rc-1',
        name: 'Ana Souza',
        phoneMasked: '(11) 9••••-4821',
        status: 'delivered',
        reason: '',
        at: '2026-10-20T15:04:00-03:00',
      },
      {
        id: 'sms-rc-2',
        name: 'Bruno Lima',
        phoneMasked: '(11) 9••••-1104',
        status: 'rejected',
        reason: 'Número inexistente',
        at: '2026-10-20T15:05:00-03:00',
      },
    ]),
  },
  {
    id: 'sms-enviada',
    name: 'Pontos da semana',
    from: '+12025550100',
    text: '{{primeiro_nome}}, seus {{pontos}} pontos na {{loja}} seguem ativos.',
    status: 'sent',
    audience: {
      audienceId: 'sms-semana',
      audienceName: 'Ativos da semana',
      total: 30,
      eligible: 18,
      excludedNoConsent: 6,
      excludedUnsubscribed: 3,
      excludedInvalid: 3,
    },
    scheduledAt: '2026-10-16T10:00:00-03:00',
    sendMode: 'later',
    createdAt: '2026-10-15T16:00:00-03:00',
    metrics: { sent: 18, delivered: 16, rejected: 1, unsubscribed: 1 },
    recipients: page([
      {
        id: 'sms-rc-3',
        name: 'Carla Dias',
        phoneMasked: '(21) 9••••-7730',
        status: 'delivered',
        reason: '',
        at: '2026-10-16T10:02:00-03:00',
      },
      {
        id: 'sms-rc-4',
        name: 'Diego Martins',
        phoneMasked: '(21) 9••••-2208',
        status: 'unsubscribed',
        reason: 'Pediu para sair',
        at: '2026-10-16T11:12:00-03:00',
      },
    ]),
  },
  {
    id: 'sms-pausada',
    name: 'Reativação curta',
    from: '+12025550100',
    text: '{{primeiro_nome}}, a {{loja}} sente sua falta.',
    status: 'paused',
    pausedFrom: 'scheduled',
    audience: {
      audienceId: 'sms-reativacao',
      audienceName: 'Sem compra há 45 dias',
      total: 40,
      eligible: 22,
      excludedNoConsent: 8,
      excludedUnsubscribed: 6,
      excludedInvalid: 4,
    },
    scheduledAt: '2026-10-27T09:30:00-03:00',
    sendMode: 'later',
    createdAt: '2026-10-17T12:00:00-03:00',
  },
  {
    id: 'sms-falha',
    name: 'Aviso de saldo',
    from: '+12025550100',
    text: '{{primeiro_nome}}, confira seus pontos na {{loja}}.',
    status: 'failed',
    audience: {
      audienceId: 'sms-saldo',
      audienceName: 'Saldo acima de 500',
      total: 20,
      eligible: 10,
      excludedNoConsent: 4,
      excludedUnsubscribed: 3,
      excludedInvalid: 3,
    },
    scheduledAt: '2026-10-14T08:00:00-03:00',
    sendMode: 'later',
    createdAt: '2026-10-13T19:20:00-03:00',
    failureSummary: 'O lote foi rejeitado nesta demonstração. Nenhum SMS saiu de verdade.',
    metrics: { sent: 10, delivered: 0, rejected: 10, unsubscribed: 0 },
  },
  {
    id: 'sms-cancelada',
    name: 'Teste de texto',
    from: '+12025550100',
    text: 'Mensagem interna da {{loja}}.',
    status: 'cancelled',
    audience: {
      audienceId: 'sms-teste',
      audienceName: 'Lista de teste',
      total: 8,
      eligible: 4,
      excludedNoConsent: 2,
      excludedUnsubscribed: 1,
      excludedInvalid: 1,
    },
    scheduledAt: '2026-10-09T15:00:00-03:00',
    sendMode: 'later',
    createdAt: '2026-10-09T14:40:00-03:00',
  },
];
